import {useState} from 'react';
import {Button, Form, Spinner} from 'react-bootstrap';
import {Link, useNavigate} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import {userLogin} from '../../../api/apiClient';
import {useAuth} from '../../../context/AuthContext';
import {useModal} from '../../../context/ModalContext';
import GoogleAnalytics from '../../google-analytics/GoogleAnalytics';
import Classes from '../../join-form/JoinForm.module.scss';
import JoinTermsCondition from '../join-terms-condition/JoinTermsCondition';
//== icons
import showPassIcon from '../../../assets/icons/showPass.svg';
import closePassIcon from '../../../assets/icons/closePass.svg';


const Login = (props: any) => {
    const {showPasswordLogin, handleShowPasswordLogin} = props;
    const {setStoredToken} = useAuth();
    const {handleJoinFormClose} = useModal();
    const {register, handleSubmit, formState: {errors}, reset}: any = useForm({
        mode: 'all', defaultValues: {
            email: localStorage.username,
            password: localStorage.password
        }
    });
    const [loginErrMessage, setLoginErrMessage] = useState('');
    const navigate = useNavigate();
    const [rememberMe, setRememberMe] = useState({
        email: "",
        password: "",
        isChecked: false
    });

    const onChangeCheckbox = (event: any) => {
        setRememberMe({
            ...rememberMe,
            isChecked: event.target.checked
        });
    };


    const {mutate, isLoading}: any = useMutation(userLogin);
    const logIn = async (loginData: any) => {
        if (rememberMe.isChecked && rememberMe.email !== "") {
            localStorage.username = rememberMe.email;
            localStorage.password = rememberMe.password;
            localStorage.checkbox = rememberMe.isChecked;
        }
        try {
            await mutate(loginData, {
                onSuccess: (loginRes: any) => {
                    if (loginRes?.data?.status === 200) {
                        setStoredToken(loginRes?.data?.jwt);
                        reset();
                        handleJoinFormClose();
                        navigate('/dashboard/uploads');
                        GoogleAnalytics.sendEvent(
                            'User login with valid account',
                            'User login',
                            'User login successfully'
                        );
                    } else {
                        setLoginErrMessage(loginRes?.data?.message);
                        GoogleAnalytics.sendEvent(
                            'User login with valid account',
                            'User login',
                            `User login failed with ${loginRes?.data?.message}`
                        );
                    }
                },
                onError: (err: any) => {
                    GoogleAnalytics.sendEvent(
                        'User login with valid account',
                        'User login',
                        `User login failed with ${err?.message}`
                    );
                }
            });
        } catch (err: any) {
            GoogleAnalytics.sendEvent(
                'User login with valid account',
                'User login',
                `User login failed with ${err?.message}`
            );
        }
    };

    return (
        <>
            <Form
                className={'text-start'}
                onSubmit={handleSubmit(logIn)}
            >
                <Form.Group controlId={'loginEmail'}>
                    <Form.Control
                        autoComplete={'off'}
                        type="email"
                        placeholder="Enter email"
                        className={`${errors.email ? 'error-input' : ''}`}
                        defaultValue={rememberMe.email}
                        {...register('email', {
                            required: 'Enter email address',
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                message: "Enter a valid e-mail address",
                            },
                            onChange: (e: any) => {
                                setLoginErrMessage('');
                                setRememberMe({
                                    ...rememberMe,
                                    email: e.target.value
                                });
                            }
                        })}
                    />
                    {errors.email && (<p className={'error-input-message'}>{errors.email.message}</p>)}
                </Form.Group>

                <Form.Group className={`${Classes.passwordGroup}`} controlId={'loginPassword'}>
                    <Form.Control
                        type={showPasswordLogin ? 'text' : 'password'}
                        placeholder="Password"
                        defaultValue={rememberMe.password}
                        className={`${errors.password ? 'error-input' : ''}`}
                        {...register('password', {
                            required: true,
                            onChange: (e: any) => {
                                setLoginErrMessage('');
                                setRememberMe({
                                    ...rememberMe,
                                    password: e.target.value
                                });
                            }
                        })}
                    />
                    <button className={Classes.showPassBtn} onClick={handleShowPasswordLogin} type={'button'}>
                        {showPasswordLogin ?
                            (<img src={showPassIcon} alt="Show"/>)
                            : (<img src={closePassIcon} alt="Close"/>)}
                    </button>
                </Form.Group>

                {loginErrMessage && (<p className={'error-input-message'}>{loginErrMessage}</p>)}

                <div className={`${Classes.forgotPassLink} d-flex align-items-center mt-2`}>
                    <Link
                        to={'/forgot-password'}
                        onClick={handleJoinFormClose}
                        className={'ms-auto'}
                    >
                        Forgot your password?
                    </Link>
                </div>

                <div className={Classes.rememberMe}>
                    <Form.Check
                        type={'checkbox'}
                        id={'remember-password'}
                        defaultChecked={localStorage.checkbox}
                        label={`Remember me`}
                        bsPrefix={`${Classes.formCheck} form-check mt-3`}
                        onChange={onChangeCheckbox}
                    />
                </div>

                <Button
                    variant="secondary"
                    type="submit"
                    bsPrefix={'g-btn'}
                    className={`${Classes.submitBtn} w-100 mt-3 mt-lg-4`}
                    disabled={isLoading}
                >
                    {isLoading ? (<Spinner animation="border" size="sm"/>) : 'LOG IN'}
                </Button>
            </Form>
            <JoinTermsCondition/>
        </>
    );
};

export default Login;